import MoreVertIcon from '@mui/icons-material/MoreVert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AthenaStatusChip from '../../../shared-theme/customizations/AthenaStatusChip';
import AttachmentsCell from './AttachmentsCell';

// Enable UTC plugin
dayjs.extend(utc);

// Date formatting helper
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = dayjs.utc(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format('MM/DD/YYYY');
}

export const getProjectColumns = ({
  menuAnchorEl,
  menuRowId,
  setMenuAnchorEl,
  setMenuRowId,
  onViewProject,
  dispatch,
  openEditModal,
  setProjectToArchive,
  setArchiveDialogOpen,
  setDownloadProject,
  // ACL permission check function - receives (actionName, projectId, programId)
  checkPermission,
}) => [
  {
    field: 'name',
    headerName: 'Project Name',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'code',
    headerName: 'Code',
    width: 120,
    valueGetter: (value) => value || '-',
  },
  // Description and Creation Date are still dropped from the table —
  // both surface in the right-side View Panel when a row is selected.
  {
    field: 'lastModified',
    headerName: 'Last Modified',
    width: 150,
    valueGetter: (value) => (value ? formatDate(value) : '-'),
  },
  {
    field: 'ownerName',
    headerName: 'Owner',
    width: 150,
    valueGetter: (value) => value || '-',
  },
  {
    field: 'workflowState',
    headerName: 'Status',
    width: 180,
    renderCell: (params) => <AthenaStatusChip workflowState={params.value} />,
  },
  {
    field: 'attachments',
    headerName: 'Attachments',
    width: 130,
    sortable: false,
    renderCell: (params) => (
      <AttachmentsCell projectId={params.row.projectId} />
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    sortable: false,
    renderCell: (params) => {
      const open = Boolean(menuAnchorEl) && menuRowId === params.row.id;
      const projectId = params.row.projectId;
      const programId = params.row.project?.programId;

      // Check permissions for this specific project
      const canView = checkPermission
        ? checkPermission('get-project', projectId, programId)
        : true;
      const canEdit = checkPermission
        ? checkPermission('update-project', projectId, programId)
        : true;
      const canArchive = checkPermission
        ? checkPermission('archive-project', projectId, programId)
        : true;
      const canDownload = checkPermission
        ? checkPermission('download-project', projectId, programId)
        : true;
      // const canPrint = checkPermission ? checkPermission('print-project', projectId, programId) : true;
      const canCopyUrl = true; // Copy URL is always available

      // Check if any actions are available
      const hasAnyAction =
        canView || canEdit || canArchive || canDownload || canCopyUrl;

      // If no actions available, don't show the menu button
      if (!hasAnyAction) {
        return null;
      }

      return (
        <>
          <Tooltip title="Project actions" arrow>
            <IconButton
              aria-label="Project actions"
              aria-controls={open ? `actions-menu-${params.row.id}` : undefined}
              aria-haspopup="true"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click event
                setMenuAnchorEl(e.currentTarget);
                setMenuRowId(params.row.id);
              }}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>

          <Menu
            id={`actions-menu-${params.row.id}`}
            anchorEl={menuAnchorEl}
            open={open}
            onClose={() => {
              setMenuAnchorEl(null);
              setMenuRowId(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {/* Order mirrors the Library kebab convention:
                Modify (View, Edit) → Share (Download, Copy Link) → divider →
                Destructive (Delete Project). Keeps the action grammar consistent
                across pages and isolates the destructive option below a divider. */}
            {canView && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  setMenuAnchorEl(null);
                  setMenuRowId(null);
                  if (onViewProject) {
                    onViewProject(params.row);
                  }
                }}
              >
                View Details
              </MenuItem>
            )}
            {canEdit && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  setMenuAnchorEl(null);
                  setMenuRowId(null);
                  // Prepare project data for editing
                  const projectData = {
                    title: params.row.name,
                    projectCode: params.row.code,
                    programId: params.row.project?.programId || '',
                    ownerId:
                      params.row.ownerUserId ||
                      params.row.project?.ownerId ||
                      '',
                    locationId: params.row.project?.locationId || '',
                    description: params.row.description || '',
                    projectFilePath: params.row.project?.projectFilePath || '',
                  };
                  dispatch(
                    openEditModal({
                      projectId: params.row.projectId,
                      projectData,
                    }),
                  );
                }}
              >
                Edit Project
              </MenuItem>
            )}
            {canDownload && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  setMenuAnchorEl(null);
                  setMenuRowId(null);
                  setDownloadProject(params.row);
                }}
              >
                Download Files
              </MenuItem>
            )}
            {canCopyUrl && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  setMenuAnchorEl(null);
                  setMenuRowId(null);
                  // Copy project URL to clipboard (using hash router format)
                  const projectUrl = `${window.location.origin}/#/projects?projectId=${params.row.projectId}`;
                  navigator.clipboard
                    .writeText(projectUrl)
                    .then(() => {
                      // Optional: Show success notification
                      console.log('Project URL copied to clipboard');
                    })
                    .catch((err) => {
                      console.error('Failed to copy URL:', err);
                    });
                }}
              >
                Copy Link
              </MenuItem>
            )}
            {canArchive &&
              (canView || canEdit || canDownload || canCopyUrl) && (
                <Divider sx={{ my: 0.5 }} />
              )}
            {canArchive &&
              (() => {
                // Check if any real (non-debug) deliverable has an approved workflow state
                const deliverables = params.row.deliverables || [];
                const hasApprovedDeliverable = deliverables.some(
                  (d) =>
                    !d.debug &&
                    d.workflowState &&
                    d.workflowState.toLowerCase().includes('approved'),
                );
                // Fail-closed: if the deliverables fetch errored, we can't be
                // sure none are approved, so block deletion as a safety measure.
                const hasApprovedEntity =
                  hasApprovedDeliverable ||
                  params.row.hasApprovedForms ||
                  params.row.deliverablesFetchFailed;

                const menuItem = (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click event
                      if (hasApprovedEntity) {
                        return;
                      }
                      setMenuAnchorEl(null);
                      setMenuRowId(null);
                      // Open confirmation dialog with project info
                      setProjectToArchive({
                        projectId: params.row.projectId,
                        projectName: params.row.name,
                        projectCode: params.row.code,
                      });
                      setArchiveDialogOpen(true);
                    }}
                    disabled={hasApprovedEntity}
                  >
                    Delete Project
                  </MenuItem>
                );

                if (hasApprovedEntity) {
                  return (
                    <Tooltip
                      title="Cannot delete project with approved deliverables"
                      arrow
                    >
                      <span>{menuItem}</span>
                    </Tooltip>
                  );
                }

                return menuItem;
              })()}
            {/* {canPrint && (
              <MenuItem
                onClick={() => {
                  // TODO: wire Print action
                  // console.log('Print', params.row.projectId);
                  setMenuAnchorEl(null);
                  setMenuRowId(null);
                }}
              >
                Print
              </MenuItem>
            )} */}
          </Menu>
        </>
      );
    },
  },
];
